import {defineStore} from "pinia";
import axios from "axios";
import {changeLoadApp, changeShow, setProject} from "@/composables/ModuleCommunication";
import {useStore} from "@/store/index";
import {useUser} from "@/store/user";
import { t } from "@/controllerLK/GlobusLocalization";

export const useProjects = defineStore({
    id: "projectsStore",
    state: () => ({
        archivedProjects: [],
        projects: [],
        activeProject: {map_files: [], modules: []},
        countries: [],
        foundedCountryId: "",
        foundedRegionList: []
    }),
    actions: {
        async getProject(id){
            const mainStore = useStore();

            try {
                const result = await axios.get(`/globus/api/projects/${id}`);
                return result.data;
            } catch (e) {
                mainStore.createNotify(t("notification.get_project.error"), "error");
                return false;
            }
        },

        async updateAccessToProject(projectId, userList) {
            try {
                await axios.put(
                  `/globus/api/User/project/access/${projectId}/update`,
                  userList
                );
            } catch (e) {
                console.log(e);
            }
        },

        async getProjects(isArchived = false){
            const mainStore = useStore();

            try {
                const response = await axios.get(`/globus/api/projects?archival=${isArchived}`);
                isArchived ? this.archivedProjects = response.data : this.projects = response.data;
            } catch (e) {
                mainStore.createNotify(t("notification.get_project.error"), "error");
            }

        },

        async createProject(data){
            const mainStore = useStore();

            try {
                const result = await axios.post("/globus/api/projects", data);
                this.projects.push(result.data);
                mainStore.createNotify(t("notification.project_created.message"), "success");

                if (this.projects.length === 1) {
                    changeShow(true);
                    changeLoadApp(true);
                    await this.setActiveProject(result.data.id);
                }

                return result.data;
            } catch (e) {
                mainStore.createNotify(t("notification.project_created.error"), "error");
            }
        },
        async updateProject(data){
            const mainStore = useStore();
            const userStore = useUser();

            try {
                await axios.put(`/globus/api/projects/${data.id}`, data);
                mainStore.createNotify(t("lk_project_modal.toast.success", data.name), "success");

                await userStore.getUser();
                if (userStore.user.activeProject === 0) {
                    this.activeProject = userStore.user.activeProject;
                    changeLoadApp(false);
                    changeShow(false);
                } else {
                    await this.setActiveProject(userStore.user.activeProject);
                }
            } catch (e) {
                mainStore.createNotify(t("notification.save.error"), "error");
            }
        },
        async deleteProject(id, isArchived, isProjectOwner){
            const mainStore = useStore();
            const userStore = useUser();

            if (!isProjectOwner) {
                mainStore.createNotify(t("notification.delete_active_project.not_owner"), "error");
                return false;
            }

            try {
                await axios.delete(`/globus/api/projects/${id}`);
                await this.getProjects(isArchived);

                if (id === this.activeProject.id) {
                    if (this.projects.length > 0) {
                        await this.setActiveProject(this.projects[0].id);
                    } else {
                        await userStore.getUser();
                        setProject(null);
                        this.activeProject = {map_files: [], modules: []};
                    }
                }

                mainStore.createNotify(t("notification.project_delete.message"), "success");
            } catch (e) {
                mainStore.createNotify(t("notification.project_delete.error"), "error");
            }
        },

        async setActiveProject(id) {
            const mainStore = useStore();
            const userStore = useUser();

            try {
                userStore.activeProject = id;
                this.activeProject = await this.getProject(id);
                setProject(this.activeProject);

                if (this.projects.length === 1) {
                    changeShow(true);
                    changeLoadApp(true);
                }

            } catch (e) {
                mainStore.createNotify(t("notification.set_active_project.error"), "error");
            }
        },
        async searchRegion(value) {

            try {
                const result = await axios.get(`/globus/api/MapFile/locations/${value}`);
                this.foundedRegionList = result.data;
            } catch (e) {
                this.foundedRegionList = [];
                console.log(e);
            }

            return this.foundedRegionList;
        },

        async createMap(projectId, locationId, mapName) {
            const mainStore = useStore();

            try {
                const result = await axios.post(
                    `/globus/api/MapFile/projects/${projectId}/maps/${locationId}`,
                    { mapName: mapName.trim() },
                    { timeout: 300000 } // 5 минут
                );

                const project = this.projects.find(p => p.id === projectId);

                if (!project) return;

                // Проверяем, что map_files существует
                if (!project.map_files) project.map_files = [];
                // Добавляем новую карту
                project.map_files.push(result.data);

                return result.data;
            } catch (error) {
                mainStore.createNotify(t("notification.error_list.bad_request"), "error");
            }
        },

        async uploadMap(projectId, map) {
            const mainStore = useStore();

            try {
                const formData = new FormData();
                formData.append("formFile", map);
                formData.append("MapName", map.name);

                const response = await axios.post(
                    `/globus/api/MapFile/projects/${projectId}/maps/upload`,
                    formData
                );

                const project = this.projects.find(p => p.id === projectId);

                if (!project) return;

                // Проверяем, что map_files существует
                if (!project.map_files) project.map_files = [];
                // Добавляем новую карту
                project.map_files.push(response.data);

                return response.data;
            } catch (error) {
                mainStore.createNotify(t("notification.error_list.bad_request"), "error");
            }
        },

        async deleteProjectMap(projectId, map) {
            await axios.delete(`/globus/api/MapFile/projects/${projectId}/maps/${map.name}`);

            const projectInStore = this.projects.filter((project) => project.id === projectId);
            projectInStore[0].map_files.splice(projectInStore[0].map_files.indexOf(map), 1);

            if (projectId === this.activeProject.id) {
                setProject(projectInStore[0]);
                this.activeProject = projectInStore[0];
            }
        },

        // Запрос получения списка тем по ID проекта
        // Не удалять. Сделано заранее, когда бек перейдет на темы вместо рубрик.
        async getProjectThemes(projectId) {
            const url = `/globus/api/projects/${projectId}/blocks/`;

            try {
                const response = await axios.get(url);
                return response.data;
            } catch (e) {
                console.log(e);
                return [];
            }
        },

        // Запрос получения списка рубрик по ID проекта
        async getProjectRubrics(projectId) {
            const url = `/globus/api/projects/${projectId}/rubrics/`;

            try {
                const response = await axios.get(url);
                return response.data;
            } catch (e) {
                console.log(e);
                return [];
            }
        },

        clearData() {
            this.projects = [];
            this.archivedProjects = [];
            this.activeProject = {map_files: [], modules: []};
            this.countries = [];
            this.foundedCountryId = "";
            this.foundedRegionList = [];

            setProject(""); // очистка юзера в приложение
        }
    }
});
